// Aperçu figé : on rasterise le PDF hors écran et on ne remplace les pages
// qu'une fois l'image prête. PDFViewer recharge une iframe sombre à chaque frappe.
import { useEffect, useRef, useState } from 'react';
import { getDocument, GlobalWorkerOptions, version as pdfjsVersion } from 'pdfjs-dist';
import { buildDocumentPdfBlob, type DocumentPdfProps } from './DocumentPdf';

GlobalWorkerOptions.workerSrc = `${import.meta.env.BASE_URL}pdf.worker.min.js?v=${pdfjsVersion}`;

const CSS_WIDTH = 720;

async function renderPages(props: DocumentPdfProps): Promise<string[]> {
  const blob = await buildDocumentPdfBlob(props);
  const pdf = await getDocument({ data: await blob.arrayBuffer() }).promise;
  const urls: string[] = [];
  try {
    const outputScale = Math.min(window.devicePixelRatio || 1, 2);
    for (let n = 1; n <= pdf.numPages; n++) {
      const page = await pdf.getPage(n);
      const unscaled = page.getViewport({ scale: 1 });
      const viewport = page.getViewport({ scale: CSS_WIDTH / unscaled.width });
      const canvas = document.createElement('canvas');
      canvas.width = Math.floor(viewport.width * outputScale);
      canvas.height = Math.floor(viewport.height * outputScale);
      const ctx = canvas.getContext('2d');
      if (!ctx) continue;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.setTransform(outputScale, 0, 0, outputScale, 0, 0);
      const task = page.render({ canvas, canvasContext: ctx, viewport });
      await task.promise;
      const png = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
      if (png) urls.push(URL.createObjectURL(png));
    }
  } finally {
    await pdf.cleanup();
  }
  return urls;
}

export function LetterPreview({ props }: { props: DocumentPdfProps }) {
  const [pages, setPages] = useState<string[]>([]);
  const [updating, setUpdating] = useState(true);
  const pagesRef = useRef<string[]>([]);
  const genRef = useRef(0);

  useEffect(() => {
    const gen = ++genRef.current;
    setUpdating(true);
    let cancelled = false;
    renderPages(props)
      .then((urls) => {
        if (cancelled || gen !== genRef.current) {
          urls.forEach((u) => URL.revokeObjectURL(u));
          return;
        }
        const previous = pagesRef.current;
        pagesRef.current = urls;
        setPages(urls);
        setUpdating(false);
        previous.forEach((u) => URL.revokeObjectURL(u));
      })
      .catch(() => {
        if (!cancelled && gen === genRef.current) setUpdating(false);
      });
    return () => {
      cancelled = true;
    };
  }, [props]);

  useEffect(() => () => {
    pagesRef.current.forEach((u) => URL.revokeObjectURL(u));
  }, []);

  return (
    <div className="flex h-full min-h-[480px] flex-col overflow-hidden rounded-cime border border-neutral-200 bg-neutral-100">
      <div className="flex items-center justify-between px-3 py-2">
        <p className="text-xs font-medium text-neutral-500">Aperçu du document</p>
        {updating && pages.length > 0 && (
          <p className="text-xs text-neutral-400">Mise à jour…</p>
        )}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-3">
        {pages.length === 0 ? (
          <p className="py-16 text-center text-sm text-neutral-400">Préparation de l’aperçu…</p>
        ) : (
          <div className="mx-auto flex max-w-[640px] flex-col gap-3">
            {pages.map((src) => (
              <img key={src} src={src} alt="" className="w-full bg-white shadow-sm" />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
